import { useEffect, useState, useMemo } from 'react';
import { Tag, Card, message } from 'antd';
import { getAdminOperationLogs, getAdminLockLogs } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import UserType from '../../enums/UserType';
import {
  OPER_LOG_METER_MATCH_OPTIONS,
  OPER_LOG_SEARCH_FIELD_OPTIONS,
} from '../../utils/listSearch';

const MODE_CONFIG = {
  electric: {
    title: '电表操作记录',
    deviceType: 1,
    kind: 'meter',
  },
  water: {
    title: '水表操作记录',
    deviceType: 2,
    kind: 'meter',
  },
  lock: {
    title: '门锁操作记录',
    kind: 'lock',
  },
};

function statusTagColor(text) {
  if (text === '成功') return 'success';
  if (text === '失败') return 'error';
  if (text === '待回执') return 'processing';
  return 'default';
}

const OperationLogList = ({ mode = 'electric' }) => {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.electric;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('meter_no');
  const [meterMatch, setMeterMatch] = useState('prefix');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchList = async (search, page, pageSize, field = searchField, match = meterMatch) => {
    setLoading(true);
    try {
      const searchOpts = { searchField: field, meterMatch: match };
      const res =
        config.kind === 'lock'
          ? await getAdminLockLogs(search, page, pageSize, searchOpts)
          : await getAdminOperationLogs(config.deviceType, search, page, pageSize, searchOpts);

      if (res?.success && Array.isArray(res.data)) {
        setData(res.data);
        setPagination((prev) => ({
          ...prev,
          current: res.page ?? page,
          pageSize: res.page_size ?? pageSize,
          total: Number(res.total) || 0,
        }));
      } else {
        setData([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
        message.error('操作记录数据格式错误');
      }
    } catch (err) {
      console.error('加载操作记录失败：', err);
      // 提示已由 api 响应拦截器弹出；此处只保留列表，避免清空后看起来像「搜不到」
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchText('');
    setSearchField('meter_no');
    setMeterMatch('prefix');
    fetchList('', 1, 20, 'meter_no', 'prefix');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mode 切换时重载
  }, [mode]);

  const handleSearch = () => {
    fetchList(searchText.trim(), 1, pagination.pageSize, searchField, meterMatch);
  };

  const handleReset = () => {
    setSearchText('');
    setSearchField('meter_no');
    setMeterMatch('prefix');
    fetchList('', 1, pagination.pageSize, 'meter_no', 'prefix');
  };

  const handleRefresh = () => {
    fetchList(searchText.trim(), pagination.current, pagination.pageSize, searchField, meterMatch);
  };

  const handleSearchFieldChange = (field) => {
    setSearchField(field);
  };

  const handleMeterMatchChange = (match) => {
    setMeterMatch(match);
  };

  const handleTableChange = (newPagination) => {
    fetchList(
      searchText.trim(),
      newPagination.current,
      newPagination.pageSize,
      searchField,
      meterMatch
    );
  };

  const searchPlaceholder =
    searchField === 'device_id'
      ? '请输入设备ID（精确）'
      : meterMatch === 'exact'
        ? '请输入完整表号（精确）'
        : '请输入表号（默认前缀匹配）';

  const meterColumns = useMemo(
    () => [
      { title: '操作时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
      { title: '设备ID', dataIndex: 'device_id', key: 'device_id', width: 100 },
      { title: '表号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 140 },
      { title: '日志表号', dataIndex: 'meter_no', key: 'meter_no', width: 140 },
      { title: '房源', dataIndex: 'house_name', key: 'house_name', width: 140, render: (v) => v || '-' },
      { title: '房号', dataIndex: 'room_fang_hao', key: 'room_fang_hao', width: 100, render: (v) => v || '-' },
      { title: '操作类型', dataIndex: 'operation', key: 'operation', width: 120 },
      { title: '操作内容', dataIndex: 'content', key: 'content', width: 200, ellipsis: true, render: (v) => v || '-' },
      {
        title: '操作人',
        key: 'operator',
        width: 160,
        render: (_, row) => {
          const phone = row.operator_phone || '';
          const role = row.operator_role_text || '';
          if (!phone && !role) return '-';
          return role ? `${phone || '-'} (${role})` : phone;
        },
      },
      {
        title: '状态',
        dataIndex: 'status_text',
        key: 'status_text',
        width: 90,
        render: (text) => <Tag color={statusTagColor(text)}>{text || '-'}</Tag>,
      },
    ],
    []
  );

  const lockColumns = useMemo(
    () => [
      { title: '操作时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
      { title: '设备ID', dataIndex: 'device_id', key: 'device_id', width: 100 },
      { title: '表号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 140, render: (v) => v || '-' },
      { title: '房源', dataIndex: 'house_name', key: 'house_name', width: 140, render: (v) => v || '-' },
      { title: '房号', dataIndex: 'room_fang_hao', key: 'room_fang_hao', width: 100, render: (v) => v || '-' },
      { title: '开锁方式', dataIndex: 'method_text', key: 'method_text', width: 120 },
      {
        title: '开锁人身份',
        dataIndex: 'user_type',
        key: 'user_type',
        width: 100,
        render: (v) => UserType.getText(v),
      },
      { title: '操作人', dataIndex: 'operator_phone', key: 'operator_phone', width: 140, render: (v) => v || '-' },
      {
        title: '结果',
        dataIndex: 'success_text',
        key: 'success_text',
        width: 90,
        render: (text) => <Tag color={statusTagColor(text)}>{text || '-'}</Tag>,
      },
    ],
    []
  );

  const originalColumns = config.kind === 'lock' ? lockColumns : meterColumns;
  const storageKey = `operationLogList_${mode}`;
  const [displayColumns, setDisplayColumns] = useState([]);

  // 从 localStorage 加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const columnStatesKey = `${storageKey}_columnStates`;
    const saved = localStorage.getItem(columnStatesKey);
    let columnStates = {};

    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('解析操作记录列设置失败：', e);
      }
    }

    const processedColumns = originalColumns
      .map((col) => {
        if (columnStates[col.key] === 'hidden') {
          return null;
        }
        return col;
      })
      .filter((col) => col !== null);

    setDisplayColumns(processedColumns);
  }, [originalColumns, storageKey]);

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title={config.title} />
      <Card bordered={false} style={{ marginTop: 0 }}>
        <ListToolbar
          searchPlaceholder={searchPlaceholder}
          searchValue={searchText}
          onSearchChange={setSearchText}
          searchScopeOptions={OPER_LOG_SEARCH_FIELD_OPTIONS}
          searchScope={searchField}
          onSearchScopeChange={handleSearchFieldChange}
          showMatchMode={searchField === 'meter_no'}
          matchMode={meterMatch}
          matchModeOptions={OPER_LOG_METER_MATCH_OPTIONS}
          onMatchModeChange={handleMeterMatchChange}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
        />

        <ResizableTable
          columns={displayColumns.length > 0 ? displayColumns : originalColumns}
          dataSource={data}
          loading={loading}
          storageKey={storageKey}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          rowKey="id"
        />
      </Card>
    </div>
  );
};

export default OperationLogList;
